import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import { DescriptionTable, List, Page, SearchForm } from 'shared/components';
import { displayValue } from 'shared/helpers';

const CustomerAccountsList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const customerAccounts = useSelector((state) =>
    get(state, 'account.customerAccounts', []),
  );
  const loading = useSelector((state) => get(state, 'account.loading'));
  const [tableExpanded, setTableExpanded] = useState({});
  const [activeQuery, setActiveQuery] = useState('');
  const [searchBy, setSearchBy] = useState('all');

  const normalizeValue = (value) => `${value ?? ''}`.toLowerCase();
  const filteredAccounts = useMemo(() => {
    const query = activeQuery.trim().toLowerCase();
    if (!query) {
      return customerAccounts;
    }

    return customerAccounts.filter((account) => {
      const fields = {
        name: get(account, 'name'),
        code: get(account, 'code'),
        reference: get(account, 'reference'),
      };
      const values =
        searchBy === 'all' ? Object.values(fields) : [fields[searchBy]];
      return values.some((value) => normalizeValue(value).includes(query));
    });
  }, [activeQuery, customerAccounts, searchBy]);

  const minRows = Math.max(Math.min(filteredAccounts.length, 20), 2);

  const getAccountEmail = (account) =>
    get(account, 'email') || get(account, 'contact_email');

  const getAccountPhone = (account) =>
    get(account, 'phone_number') || get(account, 'phone');

  const getAccountAddress = (account) => {
    const address = get(account, 'address');
    if (address) {
      return address;
    }

    const parts = [
      get(account, 'address_line1'),
      get(account, 'address_line2'),
      get(account, 'city'),
      get(account, 'state'),
      get(account, 'country'),
      get(account, 'zipcode') || get(account, 'postcode'),
    ].filter(Boolean);

    return parts.length ? parts.join(', ') : null;
  };

  const columns = [
    {
      expander: true,
      Header: t('Details'),
      headerClassName: 'text-center',
      width: 80,
      style: {
        fontSize: 25,
        padding: '0',
        textAlign: 'center',
        userSelect: 'none',
      },
    },
    {
      accessor: 'name',
      Header: t('Name'),
      sortable: true,
    },
    {
      accessor: 'code',
      Header: t('Code'),
      sortable: true,
    },
    {
      accessor: 'customers_count',
      Header: t('Number of customers'),
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (cellData) => displayValue(cellData.value),
      width: 160,
    },
    {
      accessor: 'email',
      Header: t('Email'),
      Cell: (cellData) => displayValue(getAccountEmail(cellData.original)),
    },
    {
      accessor: 'reference',
      Header: t('Reference'),
      Cell: (cellData) => displayValue(cellData.value),
    },
  ];

  const handleClear = () => setActiveQuery('');

  const handleSearchSubmit = (val) => {
    setActiveQuery(val);
  };

  const handleAccountClick = (rowData) => {
    const accountId = get(rowData, 'original.id');
    if (accountId) {
      history.push(`/customer-accounts/${accountId}`);
    }
  };

  return (
    <Page
      title={t('Customer accounts')}
      breadcrumb={<NavLink to="/">{t('Home')}</NavLink>}
    >
      <div className="CustomerAccountsList">
        <div className="list-header">
          <SearchForm
            options={[
              {
                label: t('Search by all'),
                value: 'all',
              },
              {
                label: t('Search by name'),
                value: 'name',
              },
              {
                label: t('Search by code'),
                value: 'code',
              },
              {
                label: t('Search by reference'),
                value: 'reference',
              },
            ]}
            selectedValue={searchBy}
            handleSearchTypeChange={(newSearchType) =>
              setSearchBy(newSearchType)
            }
            handleSearchSubmit={handleSearchSubmit}
            activeQuery={activeQuery}
            handleClear={handleClear}
            disabled={loading}
          />
          <div />
        </div>
        <List
          onExpandedChange={(expanded) => setTableExpanded(expanded)}
          expanded={tableExpanded}
          SubComponent={(row) => (
            <div className="SubComponent">
              <DescriptionTable
                details={[
                  {
                    label: t('Address'),
                    value: displayValue(getAccountAddress(row.original)),
                  },
                  {
                    label: t('Phone Number'),
                    value: displayValue(getAccountPhone(row.original)),
                  },
                  {
                    label: t('Description'),
                    value: displayValue(get(row, 'original.description')),
                  },
                ]}
              />
            </div>
          )}
          data={filteredAccounts || []}
          columns={columns}
          loading={loading}
          clickable
          handleClick={handleAccountClick}
          minRows={minRows}
          defaultSorted={[{ id: 'name', desc: false }]}
          showPageSizeOptions
          pageSizeOptions={[20, 50, 100]}
        />
      </div>
    </Page>
  );
};

export default CustomerAccountsList;
